"use client";

import React, { useEffect, useState } from 'react';
import { useConsent } from '@/contexts/ConsentContext';
import { useEntryFlow } from '@/contexts/EntryFlowContext';
import { firstRunStage } from '@/lib/firstRun';
import { CONSENT_STEP_GAP_MS, DOOR_TO_CONSENT_MS } from '@/components/tour/pacing';
import { FirstRunConsentModal } from './FirstRunConsentModal';
import { PostVerifyConsentScreen } from './PostVerifyConsentScreen';

/**
 * Renders whichever consent dialog the first-run sequence currently calls for,
 * and never two at once. The ORDER is not decided here: `firstRunStage` owns
 * it, and this component owns only the pacing and the rendering.
 *
 * WHERE THIS SITS IN THE FIRST RUN (owner brief, 2026-08-21): slides, portal
 * door, THEN this, THEN the guided tour. Two upstream facts enforce that.
 *
 *   - `hasOnboarded` holds the gate shut through the slide sequence. On a
 *     first run the flag now flips optimistically the moment the sequence
 *     completes (OnboardingShell), so the dialog appears in the SAME session.
 *     It used to wait for a reload to re-read the profile, which is the whole
 *     reason permissions only ever showed up on the second login.
 *   - `entrySettled` holds it shut through the door animation, so a dialog
 *     never opens behind a cinematic that is still playing.
 *
 * The delays are not decoration. A dialog that replaces another dialog in the
 * same frame reads as one dialog that glitched, not as a sequence.
 */
export const ConsentGate: React.FC = () => {
  const { loaded, hasOnboarded, hasToured, needsFirstRunConsent, needsBucketABDecision } = useConsent();
  const { entrySettled } = useEntryFlow();

  const stage = firstRunStage({
    loaded,
    entrySettled,
    hasOnboarded,
    hasToured,
    needsFirstRunConsent,
    needsBucketABDecision,
    // Consent is asked on every device; only the tour is desktop-only, and the
    // tour stage is not this component's business.
    viewportTooSmallForTour: false,
  });

  // Each dialog waits out a beat before it appears: the first one for the door
  // to have visibly finished, the second one for the first to have visibly
  // closed. `armed` is keyed on the stage so leaving and re-entering a stage
  // re-arms rather than flashing.
  const gap = stage === 'consent-required' ? DOOR_TO_CONSENT_MS : CONSENT_STEP_GAP_MS;
  const [armedStage, setArmedStage] = useState<string | null>(null);
  useEffect(() => {
    if (stage !== 'consent-required' && stage !== 'consent-optional') {
      setArmedStage(null);
      return;
    }
    const timer = window.setTimeout(() => setArmedStage(stage), gap);
    return () => window.clearTimeout(timer);
  }, [stage, gap]);

  if (armedStage !== stage) return null;
  if (stage === 'consent-required') return <FirstRunConsentModal />;
  if (stage === 'consent-optional') return <PostVerifyConsentScreen />;
  return null;
};
